import { cloneDeep } from "lodash";
import { isWebsiteBuilderTabset, IWebsiteBuilderTabset } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTabset";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderTabSettings } from "pages/secure/facility/websiteBuilder/editor/nodes/tab/websiteBuilderTab";
import { IWebsiteBuilderGeneralTraversalNode } from "../../actionHelpers/generalTraversal";
import {
  IWebsiteBuilderEditorHistoryActionBase,
  IWebsiteBuilderHistorySnapshot,
  WebsiteBuilderEditorActionType,
} from "../../websiteBuilderEditorAction";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";

export interface IUpdateTabSettingsActionPayload {
  tabsetId: string;
  tabId: string;
  prevTabSettings: IWebsiteBuilderTabSettings;
  nextTabSettings: IWebsiteBuilderTabSettings;
}

export interface IUpdateTabSettingsAction extends IWebsiteBuilderEditorHistoryActionBase {
  type: typeof WebsiteBuilderEditorActionType.UPDATE_TAB_SETTINGS;
  payload: IUpdateTabSettingsActionPayload;
}

export function getUpdateTabSettingsInverseAction(action: IUpdateTabSettingsAction): IUpdateTabSettingsAction {
  const { tabsetId, tabId, prevTabSettings, nextTabSettings } = action.payload;

  return {
    type: WebsiteBuilderEditorActionType.UPDATE_TAB_SETTINGS,
    pageId: action.pageId,
    device: action.device,
    scrollNodeId: action.scrollNodeId,
    payload: {
      tabsetId,
      tabId,
      prevTabSettings: cloneDeep(nextTabSettings),
      nextTabSettings: cloneDeep(prevTabSettings),
    },
  };
}

type TUpdateTabSettingsTraversalNode = IWebsiteBuilderTabset | IWebsiteBuilderGeneralTraversalNode;

function updateTabSettingsTraversal(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  tabsetId: string,
  tabId: string,
  settings: IWebsiteBuilderTabSettings,
): IWebsiteBuilderHistorySnapshot {
  function traverse(node: TUpdateTabSettingsTraversalNode): TUpdateTabSettingsTraversalNode {
    if (isWebsiteBuilderTabset(node)) {
      if (node.id === tabsetId) {
        return {
          ...node,
          children: node.children.map(tab => {
            if (tab.id === tabId) {
              return {
                ...tab,
                settings,
              };
            }

            return tab;
          }),
        };
      }

      return node;
    } else if (node.children) {
      return {
        ...node,
        children: node.children.map(traverse),
      };
    } else {
      return node;
    }
  }

  return {
    header: traverse(header) as IWebsiteBuilderHeader,
    root: traverse(root) as IWebsiteBuilderRoot,
    footer: traverse(footer) as IWebsiteBuilderFooter,
  };
}

export function applyUpdateTabSettingsChange(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  action: IUpdateTabSettingsAction,
): IWebsiteBuilderHistorySnapshot {
  const { tabsetId, tabId, nextTabSettings } = action.payload;

  return updateTabSettingsTraversal(header, root, footer, tabsetId, tabId, cloneDeep(nextTabSettings));
}
